import { getLogger } from '@cld/upload-widget-common';

const HAS_FILE_READ_SUPPORT = 'FileReader' in self && 'FileList' in self && 'Blob' in self;

const logger = getLogger();
const K = 1024;
const SIZE_UNITS = ['b', 'k', 'm', 'g', 't'];

const logToBase = (num, base) => Math.floor(Math.log(num) / Math.log(base));

const OFFICE_TYPES_MAP = {
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
};

const junkFiles = [
  // # All
  '^npm-debug\\.log$', // Error log for npm
  '^\\..*\\.swp$', // Swap file for vim state

  // # macOS
  '^\\.DS_Store$', // Stores custom folder attributes
  '^\\.AppleDouble$', // Stores additional file resources
  '^\\.LSOverride$', // Contains the absolute path to the app to be used
  '^Icon\\r$', // Custom Finder icon: http://superuser.com/questions/298785/icon-file-on-os-x-desktop
  '^\\._.*', // Thumbnail
  '^\\.Spotlight-V100(?:$|\\/)', // Directory that might appear on external disk
  '\\.Trashes', // File that might appear on external disk
  '^__MACOSX$', // Resource fork

  // # Linux
  '~$', // Backup file

  // # Windows
  '^Thumbs\\.db$', // Image file cache
  '^ehthumbs\\.db$', // Folder config file
  '^Desktop\\.ini$', // Stores custom folder attributes
  '@eaDir$', // Synology Diskstation "hidden" folder where the server stores thumbnails
];

export const junkFileRegexp = new RegExp(junkFiles.join('|'));

export const isJunkFile = (filename) => junkFileRegexp.test(filename);

export const notJunkFile = (filename) => !isJunkFile(filename);

const filterJunkFiles = (files) => files.filter((file) => notJunkFile((file.hdcFullPath || file.name).replace(/^.*\//, '')));

const getSizeInfoFromBytes = (bytes, decimals = 2) => {
  let text = 'na';
  let size = bytes;

  if (bytes && bytes > 0) {
    try {
      const mag = logToBase(parseInt(bytes), K);

      text = SIZE_UNITS[mag];

      if (mag > 0) {
        size = parseFloat((bytes / Math.pow(K, mag)).toFixed(decimals));
      }
    } catch (ex) {
      logger.error(`[fileUtils]: failed to parse bytes (${bytes}) into file size`, ex);
    }
  }

  return { text, size };
};

const getFileType = (file) => {
  let type = '';

  if (file.type) {
    type = OFFICE_TYPES_MAP[file.type] || file.type;
    type = type.replace(/^.*\//, '').replace(/\+.*$/, '').toUpperCase();
  } else if (file.name) {
    type = file.name.replace(/^.*\./, '').replace(/\?.*$/, '').toUpperCase();
  }

  return type.replace('JPEG', 'JPG'); // Use "jpg" as the standard file extension to display for consistency with ML
};

const VIDEO_TYPE_RGX = /^video|audio\//;
const CLD_VIDEO_TYPE_RGX = /^application\/mxf$/;
const IMAGE_TYPE_RGX = /^image\//;
const CLD_IMAGE_TYPE_RGX = /\/((x-)?photoshop|psd)$/;

const isVideo = (file) => VIDEO_TYPE_RGX.test(file.type);

const isImage = (file) => IMAGE_TYPE_RGX.test(file.type);

/**
 * whether the file type matches a mimetype that Cloudinary considers to be a video (ex: mxf)
 */
const isCloudinaryVideo = (file) => CLD_VIDEO_TYPE_RGX.test(file.type);

/**
 * whether the file type matches a mimetype that Cloudinary considers to be an image (ex: psd)
 */
const isCloudinaryImage = (file) => CLD_IMAGE_TYPE_RGX.test(file.type);

const isFile = (obj) => HAS_FILE_READ_SUPPORT && (obj instanceof File || obj.toString() === '[object File]');

const getFilePathProps = (path) => {
  const splitFileAndFolderRegex = /^(.+[/])?([^/]+)$/;
  const regexGroups = splitFileAndFolderRegex.exec(path) || [];

  regexGroups.splice(0, 1);

  const [folder, filename] = regexGroups;

  return {
    name: filename,
    folder: folder && folder.slice(0, -1),
  };
};

export { isFile, isCloudinaryImage, isCloudinaryVideo, getSizeInfoFromBytes, getFileType, isImage, isVideo, getFilePathProps, filterJunkFiles };
